import Column from "@/components/Column";
import AddressCard from "@/components/AddressCard";
import Columns from "@/components/Columns";
import Button from "@/components/Button";
import Text from "@/components/Text";
import Input from "@/components/Input";
import Select from "@/components/Select";
import useTabsValidation from "@/hooks/useTabsValidation";
import { useEffect, useState } from "react";
import { useActiveStore } from "@/store/activePages";
import { useFormStore } from "@/store/form";
import { useBlockRequestStore } from "@/store/blockRequest";
import { useUFs } from "@/api/uf";
import { useCities } from "@/api/city";
import api from "@/api";
import Layout from "@/components/Layout";
import PageHeader from "@/components/PageHeader";
import SelectItem from "@/components/Select/Item";
import { useNavigate } from "react-router-dom";
import Form from "@/components/Form";
import SEO from "@/components/SEO";
import { formatZipCode } from "@/utils";

const pageConfig = {
  route: "/address-info/unknown",
  prevRoute: "/address-info",
  nextRoute: "/address-info",
};

export type ViaCepItem = {
  index: number;
  cep: string;
  rua: string;
  bairro: string;
  cidade: string;
  uf: string;
};

type ListAddressProps = {
  addresses: ViaCepItem[];
  handleSelectAddress(address: ViaCepItem, isGeneric: boolean): void;
};

const ListAddresses = ({
  addresses,
  handleSelectAddress,
}: ListAddressProps): JSX.Element => {
  const addressGeneric = (data: ViaCepItem[]): boolean => {
    return data.length === 1 && !data[0].rua;
  };

  if (addressGeneric(addresses)) {
    return (
      <Column size={12} className="w-full pb-4">
        <AddressCard
          cep={addresses[0].cep}
          street="Endereço Generico"
          neighborhood="Sem Bairro"
          state={addresses[0].uf}
          onClick={() => handleSelectAddress(addresses[0], true)}
        />
      </Column>
    );
  }
  return (
    <Columns
      size={12}
      className="max-h-80 w-full overflow-auto pb-4"
      justifyContent="center"
      alignItems="end"
    >
      {addresses.map((address) => (
        <Column size={12} className="w-full pb-4" key={address.index}>
          <AddressCard
            cep={address.cep}
            street={address.rua}
            neighborhood={address.bairro}
            state={address.uf}
            onClick={() => handleSelectAddress(address, false)}
          />
        </Column>
      ))}
    </Columns>
  );
};

export const AddressInfoUnknown = () => {
  useTabsValidation(pageConfig.prevRoute, pageConfig.prevRoute);
  const navigate = useNavigate();
  const [uf, setUf] = useState("AC");
  const [city, setCity] = useState("");
  const [street, setStreet] = useState("");
  const [formIsValid, setFormIsValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState<ViaCepItem[] | null>(null);
  const [showMsgSelectAddresses, setShowMsgSelectAddresses] = useState(false);

  const addPage = useActiveStore((store) => store.addPage);
  const addData = useFormStore((store) => store.addData);

  const setBlockRequest = useBlockRequestStore(
    (store) => store.setBlockRequest
  );

  const { ufs } = useUFs();
  const { cities, isLoading } = useCities(uf);

  const addressFound = (data: ViaCepItem[]): boolean => {
    return data.length !== 0;
  };

  useEffect(() => {
    setCity((cities && cities[0]?.nome) || "");
  }, [cities]);

  useEffect(() => {
    setShowMsgSelectAddresses(false);
    setAddresses(null);
    setFormIsValid(!!(uf && city && street));
  }, [uf, city, street]);

  const handleOnSubmit = () => {
    const params = {
      uf,
      city,
      street,
    };
    setLoading(true);
    api
      .get("/integrations/enderecos", { params })
      .then(({ data }) => {
        if (addressFound(data)) {
          setShowMsgSelectAddresses(true);
        }
        setAddresses(data);
      })
      .finally(() => setLoading(false));
  };

  const handleSelectAddress = (item: ViaCepItem, isGeneric = false) => {
    const info = isGeneric
      ? {
          address: {
            zipCode: formatZipCode(item.cep),
            street: "",
            neighborhood: "",
            cityName: "",
            stateCode: item.uf,
          },
        }
      : {
          address: {
            zipCode: formatZipCode(item.cep),
            street: item.rua,
            neighborhood: item.bairro,
            cityName: item.cidade,
            stateCode: item.uf,
          },
        };

    addData(info);
    if (!isGeneric) {
      setBlockRequest(true);
    }
    addPage("/address-info");
    navigate("/address-info");
  };

  return (
    <Layout>
      <SEO
        title="Dados Pessoais - Complemento"
        description="Adicione os dados pessoais do titular da conta para continuar comprando seu plano TIM!"
      />
      <Column size={12}>
        <PageHeader
          title="Certo,  preencha os campos abaixo que tentaremos buscar seu cep automaticamente."
          dataIcTitle="sec-omega-comunicacao"
        />
      </Column>

      <Columns
        as={Form}
        className="pt-6"
        justifyContent="center"
        alignItems="end"
        onSubmit={() => {}}
      >
        <Column size={12} className="pb-4">
          <Select
            name="uf"
            placeholder="Estado"
            value={uf}
            onChange={(e) => setUf(e.target.value)}
          >
            {ufs?.map((item) => (
              <SelectItem key={item.id} value={item.sigla}>
                {item.nome} - {item.sigla}
              </SelectItem>
            ))}
          </Select>
        </Column>
        <Column size={12} className="pb-4">
          <Select
            name="city"
            placeholder="Cidade"
            disabled={isLoading}
            value={city}
            onChange={(e) => setCity(e.target.value)}
          >
            {cities?.map((item) => (
              <SelectItem key={item.id} value={item.nome}>
                {item.nome}
              </SelectItem>
            ))}
          </Select>
        </Column>
        <Column size={12} className="pb-4">
          <Input
            name="street"
            label="Endereço"
            placeholder="Ex. Rua Afonso Pena"
            onChange={(e) => setStreet(e.target.value.trim())}
          />
        </Column>
        {showMsgSelectAddresses && (
          <Column
            size={12}
            className="max-h-80 w-full overflow-auto px-2 pb-4 text-center"
          >
            <Text
              as="h1"
              kind="blue"
              size="base"
              className="pt-2 text-center font-bold"
            >
              Escolha um endereço
            </Text>
          </Column>
        )}
        {addresses &&
          (!addressFound(addresses) ? (
            <Text
              as="h1"
              kind="blue"
              size="base"
              className="mt-4  w-full  pt-2 font-bold"
            >
              Endereço não encontrado
            </Text>
          ) : (
            <ListAddresses
              addresses={addresses!}
              handleSelectAddress={handleSelectAddress}
            />
          ))}

        <Column size={12} className="mt-2">
          {!showMsgSelectAddresses && (
            <Button
              type="button"
              onClick={handleOnSubmit}
              large
              disabled={!formIsValid}
              helperText={
                !formIsValid ? "todos os campos são obrigatórios" : ""
              }
              loading={loading}
            >
              {!formIsValid ? "Preencha os campos para buscar" : "BUSCAR"}
            </Button>
          )}
        </Column>
        <Column size={12} className="mt-8">
          <Button
            type="button"
            kind="secondary"
            large
            to={pageConfig.prevRoute}
            data-ic-trigger="sec-omega-voltar"
          >
            Voltar
          </Button>
        </Column>
      </Columns>
    </Layout>
  );
};

export default AddressInfoUnknown;
